import React, { useState, useEffect, useCallback } from 'react';
import { analyticsService } from '@/services/analytics';
import { 
  PageViewAnalyticsContainer,
  ReferralsWidget,
  TopPagesWidget,
  CountriesWidget,
  DevicesBrowsersWidget,
  DashboardHeader
} from './components';
import { 
  AnalyticsData,
  ReferralData,
  PageData,
  DeviceData,
  BrowserData,
  CountryData,
  ActiveProperty,
  ChartData
} from './types';

interface DashboardProps {
  activeProperty?: ActiveProperty;
}

const Dashboard: React.FC<DashboardProps> = ({ activeProperty }) => {
  const [timePeriod, setTimePeriod] = useState('7');
  const [groupBy, setGroupBy] = useState('day');
  
  // State for all raw analytics data (1 year of data)
  const [rawAnalyticsData, setRawAnalyticsData] = useState<any>(null);
  const [rawReferralWebsites, setRawReferralWebsites] = useState<any>(null);
  const [rawReferralKeywords, setRawReferralKeywords] = useState<any>(null);
  const [rawTopPages, setRawTopPages] = useState<any>(null);
  const [rawDevicesBrowsers, setRawDevicesBrowsers] = useState<any>(null);
  const [rawCountries, setRawCountries] = useState<any>(null);
  
  // State for filtered data based on timePeriod and groupBy
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [referralWebsites, setReferralWebsites] = useState<ReferralData[]>([]);
  const [referralKeywords, setReferralKeywords] = useState<ReferralData[]>([]);
  const [topPages, setTopPages] = useState<PageData[]>([]);
  const [devices, setDevices] = useState<DeviceData[]>([]);
  const [browsers, setBrowsers] = useState<BrowserData[]>([]);
  const [countries, setCountries] = useState<CountryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Load all analytics data once (1 year of data)
  const loadAllAnalyticsData = useCallback(async () => {
    if (dataLoaded) return; // Don't reload if already loaded
    
    try {
      setLoading(true);
      
      // Fetch all dashboard data using analytics service
      const dashboardData = await analyticsService.fetchAllDashboardData('365', 'day');
      
      console.log('Dashboard data response:', dashboardData);
      
      // Set all the raw data
      setRawAnalyticsData(dashboardData.dailyVisits);
      setRawReferralWebsites(dashboardData.referralWebsites);
      setRawReferralKeywords(dashboardData.referralKeywords);
      setRawTopPages(dashboardData.topPages);
      setRawDevicesBrowsers(dashboardData.devicesBrowsers);
      setRawCountries(dashboardData.countries);

      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  }, [dataLoaded]);

  // Filter data based on timePeriod and groupBy
  const filterData = useCallback(() => {
    if (!dataLoaded) return;
    
    console.log('Filtering data for timePeriod:', timePeriod, 'groupBy:', groupBy);
    console.log('Raw data available:', {
      analytics: !!rawAnalyticsData,
      websites: !!rawReferralWebsites,
      keywords: !!rawReferralKeywords,
      pages: !!rawTopPages,
      devices: !!rawDevicesBrowsers,
      countries: !!rawCountries
    });

    // Filter daily visits data
    if (rawAnalyticsData) {
      let rawData: Record<string, any> = {};
      
      if (rawAnalyticsData?.data && typeof rawAnalyticsData.data === 'object') {
        rawData = rawAnalyticsData.data;
      } else if (rawAnalyticsData && typeof rawAnalyticsData === 'object' && !Array.isArray(rawAnalyticsData)) {
        const keys = Object.keys(rawAnalyticsData);
        if (keys.length > 0 && keys[0].match(/^\d{4}-\d{2}-\d{2}$/)) {
          rawData = rawAnalyticsData;
        }
      } else if (rawAnalyticsData && typeof rawAnalyticsData === 'object') {
        rawData = rawAnalyticsData;
      }

      // Filter by timePeriod
      const filteredDates = filterDatesByPeriod(Object.keys(rawData), timePeriod);
      const filteredRawData = Object.fromEntries(
        filteredDates.map(date => [date, rawData[date]]).filter(([_, data]) => data)
      );

      // Convert to chart data format
      const transformedChartData = Object.keys(filteredRawData)
        .filter(dateKey => {
          return dateKey.match(/^\d{4}-\d{2}-\d{2}$/) && filteredRawData[dateKey] && typeof filteredRawData[dateKey] === 'object';
        })
        .map(dateKey => {
          const item = filteredRawData[dateKey];
          
          return {
            date: item.date,
            pageViews: item.page_views || 0,
            visitors: item.users || 0,
            sessions: item.sessions || 0,
            avgTimeOnPage: item.avg_session_duration || 0,
            bounceRate: item.bounce_rate || 0,
          };
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Group by if needed
      const groupedChartData = groupChartData(transformedChartData, groupBy);
      setChartData(groupedChartData);
      
      // Calculate aggregated metrics
      if (groupedChartData.length > 0) {
        const totalVisitors = groupedChartData.reduce((sum, item) => sum + item.visitors, 0);
        const totalPageViews = groupedChartData.reduce((sum, item) => sum + item.pageViews, 0);
        const totalSessions = groupedChartData.reduce((sum, item) => sum + item.sessions, 0);
        
        const avgTimeOnPage = Number((groupedChartData.reduce((sum, item) => sum + item.avgTimeOnPage, 0) / groupedChartData.length).toFixed(0));
        const avgBounceRate = Number((groupedChartData.reduce((sum, item) => sum + item.bounceRate, 0) / groupedChartData.length).toFixed(2));
        
        setAnalyticsData({
          visitors: totalVisitors,
          pageViews: totalPageViews,
          timeOnPage: avgTimeOnPage,
          sessions: totalSessions,
          bounceRate: avgBounceRate
        });
      }
    }

         // Filter referral websites data
     if (rawReferralWebsites) {
       console.log('Processing referral websites data:', rawReferralWebsites);
       const filteredDates = generateDateRange(timePeriod);
       console.log('Filtered dates for websites:', filteredDates);
       
       const websiteMap = new Map<string, number>();
       
       filteredDates.forEach(date => {
         const dateData = rawReferralWebsites[date];
         if (dateData) {
           Object.entries(dateData).forEach(([source, sourceData]: [string, any]) => {
             const currentVisits = websiteMap.get(source) || 0;
             websiteMap.set(source, currentVisits + (sourceData.count || 0));
           });
         }
       });
       
       console.log('Website map:', websiteMap);
       const totalWebsiteVisits = Array.from(websiteMap.values()).reduce((sum, visits) => sum + visits, 0);
       
       const transformedWebsites: ReferralData[] = Array.from(websiteMap.entries())
         .map(([source, visits]) => ({
           source: source,
           visits: visits,
           percentage: totalWebsiteVisits > 0 ? (visits / totalWebsiteVisits) * 100 : 0
         }))
         .sort((a, b) => b.visits - a.visits)
         .slice(0, 5);
       
       console.log('Transformed websites:', transformedWebsites);
       setReferralWebsites(transformedWebsites);
     }

         // Filter referral keywords data
     if (rawReferralKeywords) {
       console.log('Processing referral keywords data:', rawReferralKeywords);
       const filteredDates = generateDateRange(timePeriod);
       console.log('Filtered dates for keywords:', filteredDates);
       
       const keywordMap = new Map<string, number>();
       
       filteredDates.forEach(date => {
         const dateData = rawReferralKeywords[date];
         if (dateData) {
           Object.entries(dateData).forEach(([keyword, keywordData]: [string, any]) => {
             const currentVisits = keywordMap.get(keyword) || 0;
             keywordMap.set(keyword, currentVisits + (keywordData.count || 0));
           });
         }
       });
       
       console.log('Keyword map:', keywordMap);
       const totalKeywordVisits = Array.from(keywordMap.values()).reduce((sum, visits) => sum + visits, 0);
       
       const transformedKeywords: ReferralData[] = Array.from(keywordMap.entries())
         .map(([keyword, visits]) => ({
           source: keyword,
           visits: visits,
           percentage: totalKeywordVisits > 0 ? (visits / totalKeywordVisits) * 100 : 0
         }))
         .sort((a, b) => b.visits - a.visits)
         .slice(0, 5);
       
       console.log('Transformed keywords:', transformedKeywords);
       setReferralKeywords(transformedKeywords);
     }

         // Filter top pages data
     if (rawTopPages) {
       console.log('Processing top pages data:', rawTopPages);
       const filteredDates = generateDateRange(timePeriod)
       console.log('Filtered dates for pages:', filteredDates);
       
       const pageViewsMap = new Map<string, number>();
       
       filteredDates.forEach(date => {
         const dateData = rawTopPages[date];
         if (dateData) {
           Object.entries(dateData).forEach(([pagePath, pageData]: [string, any]) => {
             const currentViews = pageViewsMap.get(pagePath) || 0;
             pageViewsMap.set(pagePath, currentViews + (pageData.pageviews || 0));
           });
         }
       });
       
       console.log('top pages Page views map:', pageViewsMap);
       const totalViews = Array.from(pageViewsMap.values()).reduce((sum, views) => sum + views, 0);
       
       const transformedPages: PageData[] = Array.from(pageViewsMap.entries())
         .map(([pagePath, views]) => ({
           name: pagePath,
           views: views,
           percentage: totalViews > 0 ? (views / totalViews) * 100 : 0
         }))
         .sort((a, b) => b.views - a.views)
         .slice(0, 10);
       
       console.log('Transformed pages:', transformedPages);
       setTopPages(transformedPages);
     }

         // Filter devices and browsers data
     if (rawDevicesBrowsers) {
       console.log('Processing devices and browsers data:', rawDevicesBrowsers);
       const filteredDates = generateDateRange(timePeriod);
       console.log('Filtered dates for devices/browsers:', filteredDates);
       
       const deviceMap = new Map<string, number>();
       const browserMap = new Map<string, number>();
       
       filteredDates.forEach(date => {
         const dateData = rawDevicesBrowsers[date];
         console.log("**************  dateData", dateData, rawDevicesBrowsers);
         console.log("**************  dateData", rawDevicesBrowsers[parseInt( date)]);
         if (dateData) {
           Object.entries(dateData).forEach(([key, data]: [string, any]) => {
             const count = data.count || 0;
             
             if (key === 'mobile' || key === 'desktop' || key === 'tablet') {
               const currentViews = deviceMap.get(key) || 0;
               deviceMap.set(key, currentViews + count);
             } else if (key.startsWith('browser_')) {
               const browserName = key.replace('browser_', '');
               const currentViews = browserMap.get(browserName) || 0;
               browserMap.set(browserName, currentViews + count);
             }
           });
         }
       });
       
       console.log('Device map:', deviceMap);
       console.log('Browser map:', browserMap);
       
       const totalDeviceViews = Array.from(deviceMap.values()).reduce((sum, views) => sum + views, 0);
       const transformedDevices: DeviceData[] = Array.from(deviceMap.entries())
         .map(([deviceName, views]) => ({
           device: deviceName,
           visits: views,
           percentage: totalDeviceViews > 0 ? (views / totalDeviceViews) * 100 : 0
         }))
         .sort((a, b) => b.visits - a.visits)
         .slice(0, 3);
       
       const totalBrowserViews = Array.from(browserMap.values()).reduce((sum, views) => sum + views, 0);
       const transformedBrowsers: BrowserData[] = Array.from(browserMap.entries())
         .map(([browserName, views]) => ({
           browser: browserName,
           visits: views,
           percentage: totalBrowserViews > 0 ? (views / totalBrowserViews) * 100 : 0
         }))
         .sort((a, b) => b.visits - a.visits)
         .slice(0, 3);
       
       console.log('Transformed devices:', transformedDevices);
       console.log('Transformed browsers:', transformedBrowsers);
       
       setDevices(transformedDevices);
       setBrowsers(transformedBrowsers);
     }

         // Filter countries data
     if (rawCountries) {
       console.log('Processing countries data:', rawCountries);
       console.log('Raw countries data structure:', Object.keys(rawCountries));
       console.log('Sample country data:', Object.values(rawCountries).slice(0, 2));
       console.log('countries filter dates:', Object.keys(rawCountries));
       console.log('timePeriod:', timePeriod);
       const filteredDates = generateDateRange(timePeriod);
       console.log('Filtered dates for countries:', filteredDates);
       
       const countryTotals: { [key: string]: { pageViews: number; visits: number } } = {};
       
       filteredDates.forEach(date => {
         const dateData = rawCountries[date];
         console.log(`Processing date ${date}:`, dateData);
         if (dateData) {
           Object.entries(dateData).forEach(([countryName, countryData]: [string, any]) => {
             console.log(`Country ${countryName}:`, countryData);
             if (countryName !== '(not set)' && countryData && typeof countryData === 'object') {
               const visits = countryData.count || 0;
               const pageViews = countryData.pageviews || 0;
               
               if (!countryTotals[countryName]) {
                 countryTotals[countryName] = { pageViews: 0, visits: 0 };
               }
               countryTotals[countryName].pageViews += pageViews;
               countryTotals[countryName].visits += visits;
             }
           });
         }
       });
       
       console.log('Country totals:', countryTotals);
       const totalPageViews = Object.values(countryTotals).reduce((sum, data) => sum + data.pageViews, 0);
       
       const transformedCountries: CountryData[] = Object.entries(countryTotals)
         .map(([country, data]) => ({
           country,
           visits: data.visits,
           percentage: totalPageViews > 0 ? (data.pageViews / totalPageViews) * 100 : 0
         }))
         .sort((a, b) => b.visits - a.visits)
         .slice(0, 10);
       
             console.log('Transformed countries:', transformedCountries);
      setCountries(transformedCountries);
    }
  }, [dataLoaded, timePeriod, groupBy, rawAnalyticsData, rawReferralWebsites, rawReferralKeywords, rawTopPages, rawDevicesBrowsers, rawCountries]);
  // Helper function to generate date range for timePeriod
  const generateDateRange = (period: string): string[] => {
    const today = new Date();
    let daysBack: number;

    daysBack = parseInt(period);

    const dates: string[] = [];
    for (let i = daysBack - 1; i >= 0; i--) {
      const date = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
      dates.push((date.toISOString().split('T')[0]).replace(/-/g, '')); // YYYY-MM-DD format
    }

    return dates;
  };
  
  // Helper function to filter dates by timePeriod
  const filterDatesByPeriod = (dates: string[], period: string): string[] => {
    const sortedDates = dates.sort();
    const today = new Date();
    let startDate: Date;

    switch (period) {
      case '1':
        startDate = new Date(today.getTime() - 24 * 60 * 60 * 1000); // 1 day ago
        break;
      case '7':
        startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000); // 7 days ago
        break;
      case '30':
        startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
        break;
      case '90':
        startDate = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000); // 90 days ago
        break;
      case '180':
        startDate = new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000); // 180 days ago
        break;
      case '365':
        startDate = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000); // 365 days ago
        break;
      default:
        startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000); // Default to 7 days
    }

    return sortedDates.filter(dateStr => {
      const date = new Date(dateStr);
      return date >= startDate && date <= today;
    });
  };

  // Helper function to group chart data
  const groupChartData = (data: ChartData[], groupBy: string): ChartData[] => {
    if (groupBy === 'day') {
      return data;
    }

    const grouped = new Map<string, ChartData>();

    data.forEach(item => {
      const date = new Date(item.date);
      let key: string;

      if (groupBy === 'week') {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split('T')[0];
      } else if (groupBy === 'month') {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      } else {
        return item; // Default to day
      }

      if (grouped.has(key)) {
        const existing = grouped.get(key)!;
        grouped.set(key, {
          date: key,
          pageViews: existing.pageViews + item.pageViews,
          visitors: existing.visitors + item.visitors,
          sessions: existing.sessions + item.sessions,
          avgTimeOnPage: Math.round((existing.avgTimeOnPage + item.avgTimeOnPage) / 2),
          bounceRate: Number(((existing.bounceRate + item.bounceRate) / 2).toFixed(2))
        });
      } else {
        grouped.set(key, { ...item, date: key });
      }
    });

    return Array.from(grouped.values()).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };



  // Load data once on component mount
  useEffect(() => {
    loadAllAnalyticsData();
  }, [loadAllAnalyticsData]);

  // Filter data when timePeriod or groupBy changes
  useEffect(() => {
    if (dataLoaded) {
      filterData();
    }
  }, [timePeriod, groupBy, dataLoaded, filterData]);

  // Debug logging
  console.log('Dashboard render state:', {
    loading,
    dataLoaded,
    timePeriod,
    groupBy,
    referralWebsites: referralWebsites?.length || 0,
    referralKeywords: referralKeywords?.length || 0,
    topPages: topPages?.length || 0,
    devices: devices?.length || 0,
    browsers: browsers?.length || 0,
    countries: countries?.length || 0
  });

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading skeleton for header */}
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded dark:bg-gray-700 w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded dark:bg-gray-700 w-1/4"></div>
        </div>
        
        {/* Loading skeleton for metrics and chart */}
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="h-24 bg-gray-200 rounded-lg dark:bg-gray-700"></div>
              </div>
            ))}
          </div>
          <div className="animate-pulse">
            <div className="h-80 bg-gray-200 rounded-lg dark:bg-gray-700"></div>
          </div>
        </div>
        
        {/* Loading skeleton for widgets */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="h-64 bg-gray-200 rounded-lg dark:bg-gray-700"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  console.log('===== timePeriod:', timePeriod);
  console.log('===== Countries:', countries);
  console.log('===== Referral Websites:', referralWebsites);
  console.log('===== Referral Keywords:', referralKeywords);
  console.log('===== Top Pages:', topPages);
  console.log('===== Devices:', devices);
  console.log('===== Browsers:', browsers);
  return (
    <div className="space-y-6">
      {/* Header */}
      <DashboardHeader
        propertyName={activeProperty?.name || activeProperty?.website_url || 'Your Website'}
        timePeriod={timePeriod}
        groupBy={groupBy}
        onTimePeriodChange={setTimePeriod}
        onGroupByChange={setGroupBy}
      />

      {/* Main Metrics and Chart */}
      <PageViewAnalyticsContainer 
        timePeriod={timePeriod} 
        groupBy={groupBy}
        analyticsData={analyticsData}
        chartData={chartData}
      />



      {/* Analytics Widgets */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <ReferralsWidget 
          timePeriod={timePeriod}
          websites={referralWebsites}
          keywords={referralKeywords}
        />
        <TopPagesWidget 
          timePeriod={timePeriod}
          pages={topPages}
        />
        <DevicesBrowsersWidget 
          timePeriod={timePeriod}
          devices={devices}
          browsers={browsers}
        />
        <CountriesWidget 
          timePeriod={timePeriod}
          countries={countries}
          rawData={rawCountries}
        />
      </div>
    </div>
  );
};

export default Dashboard;
